import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as TrashIcon } from '@/assets/icons/trashbin.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import Image from '@/components/Image';
import { ActionsWrap, Td } from '@/components/Table';
import useAlerts from '@/hooks/useAlerts';
import { SubtitlesGenerationProject } from '@/types/resources';
import { api } from '@/utils/api';
import { SubsGenerationProjectVideoStatus } from '@/utils/enums';
import { Color } from '@/utils/theme';

const getDotColor = (
  status: string | SubsGenerationProjectVideoStatus,
): Color => {
  if (status === 'done' || status === SubsGenerationProjectVideoStatus.DONE) {
    return 'success';
  }

  if (
    status === 'pending' ||
    status === SubsGenerationProjectVideoStatus.GENERATING
  ) {
    return 'warning';
  }

  if (
    status === SubsGenerationProjectVideoStatus.FAILED ||
    status === 'declined'
  ) {
    return 'error';
  }

  return 'gray';
};

const Thumbnail = styled(Image)`
  width: 90px;
  height: auto;
`;

const ThumbAndTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
`;

const Status = styled.div`
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  text-transform: capitalize;
`;

const StatusDot = styled.div<{
  $status: string | SubsGenerationProjectVideoStatus;
}>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-top: 5px;

  ${({ theme, $status }) => css`
    background: ${theme.colors[getDotColor($status)]};
  `}
`;

const Tr = styled.tr`
  cursor: pointer;
  transition: background-color ease-out 0.2s;
  td {
    &:last-child {
      cursor: initial;
    }
  }
  &:hover {
    td {
      transition: background-color ease-out 0.2s;
      background-color: ${({ theme }) =>
        theme.colors.grayscaleExtraLight} !important;
    }
  }
  > * {
    font-weight: 700;
    font-size: 12px;
  }
`;

interface Props {
  project: SubtitlesGenerationProject;
}

const TableRow: FC<Props> = ({ project }) => {
  const queryClient = useQueryClient();
  const alerts = useAlerts();
  const navigate = useNavigate();
  const { teamId, status } = useParams();

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete this project?`,
        project.title,
      );

      if (!confirm) return;

      await api.delete(
        `/v1/distribution/subtitles-generation-projects/${project.id}`,
      );

      await queryClient.invalidateQueries(['subtitles_generation_projects']);

      alerts.success('Project was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  const onRowClick = () => {
    navigate(
      `/teams/${teamId}/distribution/subtitles-generator/${status}/${project.id}`,
    );
  };

  const getVideoStatusLabel = () => {
    switch (project.videoStatus) {
      case SubsGenerationProjectVideoStatus.NOT_READY:
        return 'Not Ready';
      case SubsGenerationProjectVideoStatus.GENERATING:
        return 'Generating';
      case SubsGenerationProjectVideoStatus.DONE:
        return 'Done';
      case SubsGenerationProjectVideoStatus.FAILED:
        return 'Failed';
      default:
        return '-';
    }
  };

  return (
    <Tr onClick={onRowClick}>
      <Td>
        <ThumbAndTitle>
          <Thumbnail src={project.thumbnail?.versions.original} />
          <span>{project.title ?? '-'}</span>
        </ThumbAndTitle>
      </Td>
      <Td>{DateTime.fromISO(project.createdAt).toFormat('dd/LL/yyyy')}</Td>
      <Td>
        <Status>
          <StatusDot $status={project.status} />
          <span>{project.status}</span>
        </Status>
      </Td>
      <Td>
        <Status>
          <StatusDot $status={project.videoStatus} />
          {getVideoStatusLabel()}
        </Status>
      </Td>
      <Td>{project.projectId}</Td>
      <Td>{project.freshId ?? '-'}</Td>
      <Td>{project.prompt?.title ?? 'Default'}</Td>
      <Td>{project.owner ?? '-'}</Td>
      <Td $actionsCol onClick={(e) => e.stopPropagation()}>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton as="button" onClick={onDelete}>
              <span>
                <TrashIcon />
              </span>
              Delete
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
      </Td>
    </Tr>
  );
};

export default TableRow;
